import { toast } from '@tamagui/toast/v2';
import { Stack, useLocalSearchParams } from 'expo-router';
import Head from 'expo-router/head';
import { useEffect } from 'react';
import { Text, YStack } from 'tamagui';

import BookingSkeleton from '@/components/BookingSkeleton';
import CheckoutCart from '@/components/CheckoutCart';
import CheckoutEventHeader from '@/components/CheckoutEventHeader';
import CheckoutUserDetails from '@/components/CheckoutUserDetails';
import { PSHeaderBackButtonOnTransparentHeader } from '@/components/PSHeaderButtons';
import { RequestNotice } from '@/components/RequestNotice';
import Section from '@/components/Section';
import PSLinkButton from '@/components/ui/PSLinkButton';
import PSRichText from '@/components/ui/PSRichText';
import PSText from '@/components/ui/PSText';
import PageScrollView from '@/components/ui/PageScrollView';
import { UserProfile } from '@/src/api/authApi';
import type { Booking } from '@/src/api/bookingsApi';
import type { ParticipationRequestStatus } from '@/src/api/participationRequestsApi';
import { useFetchParticipationRequest } from '@/src/hooks/useFetchParticipationRequest';
import { useSessionUser } from '@/src/lib/sessionStore';
import { formatDateTime } from '@/src/utils/dateFormatter';
import { openURL } from '@/src/utils/linkingUtils';
import Footer from '@/components/Footer';

/**
 * The participation-request receipt — where confirm lands right after
 * applying to an approval-gated tier, and where the profile's "Bookings
 * pending approval" section opens one back up.
 *
 * A near-copy of the booking screen's receipt (same header, venue, slot,
 * itemised cart, your-details, terms, support), because a request already
 * carries a `Booking` — just not a confirmed one until an organizer says so.
 *
 * It renders every verdict off that same body, the way `/bookings/[id]` draws
 * a cancelled booking with the identical sections: only the headline and the
 * notice under the event header change. An APPROVED request's booking is a
 * real, confirmed one, so its notice links out to `/bookings/[id]` — the
 * ticket itself still lives there.
 */
export default function ParticipationRequestPage() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const state = useFetchParticipationRequest(id);
  const user = useSessionUser();

  // Same pattern as the booking screen: the errored state draws nothing yet,
  // so without this the message dies with the request and the screen goes
  // blank with no explanation.
  useEffect(() => {
    if (state.error) toast.error(state.error.message);
  }, [state.error]);

  if (state.status === 'loading' || user === null) {
    return <BookingSkeleton />;
  }

  // Error is unbuilt for now — render nothing rather than a half receipt.
  if (state.status === 'error') {
    return null;
  }

  return (
    <RequestBody
      status={state.request.status}
      booking={state.request.booking}
      user={user}
      socialLink={state.request.approvalQueries?.socialLink || undefined}
    />
  );
}

/**
 * The headline's two lines, per verdict, keyed by the face each is set in —
 * the same `body`/`hand` split `HEADLINE_LINE_HEIGHT` below uses. A lookup,
 * not a constant, for the same reason [[BookingPage]]'s `STATUS_HEADLINE` is
 * one: a declined request must not be handed a screen that reads "Pending".
 *
 * `blocked` reads the same as `rejected` — the two differ only in whether
 * applying again is possible, which is the notice's job to say.
 */
const STATUS_HEADLINE: Record<
  ParticipationRequestStatus,
  { body: string; hand: string }
> = {
  pending: { body: 'Pending', hand: 'Confirmation' },
  approved: { body: 'Booking', hand: 'approved' },
  rejected: { body: 'Booking', hand: 'declined' },
  blocked: { body: 'Booking', hand: 'declined' },
};

/**
 * The request body. Identical in shape to the booking screen's `BookingBody`
 * — same sections, off the same `Booking` type — plus one addition: the
 * verdict notice right under the event header.
 */
function RequestBody({
  status,
  booking,
  user,
  socialLink,
}: {
  status: ParticipationRequestStatus;
  booking: Booking;
  user: UserProfile;
  socialLink?: string;
}) {
  const { event, slot, tickets } = booking;
  const { support } = event;

  return (
    <YStack flex={1} bg="$color.background">
      <Head>
        <title>Booking request · Putting Scene</title>
      </Head>
      <Stack.Screen
        options={{
          headerTransparent: true,
          headerTitle: () => <></>,
          headerLeft: () => <PSHeaderBackButtonOnTransparentHeader />,
          unstable_headerLeftItems: () => [
            {
              type: 'custom',
              element: <PSHeaderBackButtonOnTransparentHeader />,
              hidesSharedBackground: true,
            },
          ],
        }}
      />
      <PageScrollView>
        <YStack justifyContent="center" mb={'$space.xxl'} gap={'$space.xs'}>
          <PSText.Title color={'$color.brand'}>
            {STATUS_HEADLINE[status].body}
          </PSText.Title>
          <PSText.Title>{STATUS_HEADLINE[status].hand}</PSText.Title>
        </YStack>

        <YStack gap="$space.xl">
          <CheckoutEventHeader
            imageUrl={event.images[0]?.url ?? ''}
            title={event.title}
            slotDisplay={formatDateTime(slot.startDatetime)}
            venueName={event.venue.name}
            googleMapsUrl={event.venue.googleMapsUrl}
          />

          <RequestNotice
            status={status}
            booking={booking}
            pendingCopy={
              <PSText.Body2>
                {
                  'Thanks for signing up! However, please note that your spot is '
                }
                <PSText.Body2 fontWeight="$semibold">not</PSText.Body2>
                {` confirmed yet.\n\nWe are assessing the applications, and you will receive a confirmation email on ${user.email} once ${event.orgDetails?.name ?? 'organizer'} approves your booking.`}
              </PSText.Body2>
            }
            approvedCopy={
              <PSText.Body2>
                The organizer approved your request — this ticket is already on
                your account.
              </PSText.Body2>
            }
            rejectedCopy={
              <PSText.Body2>The organizer rejected your request.</PSText.Body2>
            }
            blockedCopy={
              <PSText.Body2>
                The organizer declined your request, and this ticket is no
                longer open to you.
              </PSText.Body2>
            }
          />

          {/* Tickets — one line per requested tier. Nothing is added up here:
              every amount arrives formatted (`lineTotal.display`). */}
          <CheckoutCart
            headerLabel="Tickets"
            lines={tickets.map((ticket) => ({
              id: ticket.slotTierId,
              name: ticket.ticketTierName,
              quantity: ticket.quantity,
              subtotalDisplay: ticket.lineTotal.display,
            }))}
            totalDisplay={booking.subtotal.display}
          />

          {/* Your details — the booking contact, guaranteed present (logged in). */}
          <CheckoutUserDetails
            name={user.fullName}
            email={user.email ?? ''}
            mobile={user.mobile}
            socialLink={socialLink}
          />

          {/* Terms */}
          {event.termsAndConditions && (
            <Section headerLabel="Terms &amp; conditions">
              <PSRichText markdown={event.termsAndConditions} />
            </Section>
          )}

          {/* Support — the event's go-to contact. */}

          {support.mobile && (
            <Section headerLabel="Support">
              {/* One flowing paragraph: the link atoms nest inside the copy Text so
                          phone/email sit inline (RN renders nested pressable Text inline). */}
              <PSText.Body2>
                <Text fontWeight="$semibold">{support.name}</Text> will be your
                go-to person for any queries related to the event. You can reach
                out to them on{' '}
                <PSLinkButton
                  title={support.mobile}
                  // Strip display formatting (spaces/dashes) for the dialer, keep a
                  // leading "+"; the visible label stays the formatted number.
                  onPress={() =>
                    openURL(`tel:${support.mobile.replace(/[^\d+]/g, '')}`)
                  }
                />
                {support.email ? (
                  <>
                    {' '}
                    or{' '}
                    <PSLinkButton
                      title={support.email}
                      onPress={() => openURL(`mailto:${support.email}`)}
                    />
                  </>
                ) : null}
                .
              </PSText.Body2>
            </Section>
          )}
        </YStack>
        <Footer />
      </PageScrollView>
    </YStack>
  );
}

/**
 * The verdict notice — the one thing this screen says that the booking screen
 * never has to. Every case is reachable: a request settles on Backstage, and
 * nothing tells the client when, so any of the four can be what's on the row
 * by the time it's read.
 *
 * Typed `ReactElement`, and no `default`, so a fifth status added to
 * `ParticipationRequestStatus` is a compile error here rather than a silently
 * empty box — the same reason [[TicketTier]]'s `renderNotice` is typed that
 * way.
 */
